/**
 * WordPress dependencies
 */
import { __ } from '@wordpress/i18n';
import { Fragment, useState, useCallback, useRef } from '@wordpress/element';
import { addFilter } from '@wordpress/hooks';
import { ToolbarButton, KeyboardShortcuts, Popover } from '@wordpress/components';
import { BlockControls, __experimentalLinkControl as LinkControl } from '@wordpress/block-editor';
import { rawShortcut, displayShortcut } from '@wordpress/keycodes';
import { link, linkOff } from '@wordpress/icons';

/**
 * Inner dependencies.
 */
import blockInfo from '../constants';
const { hookPrefix } = blockInfo;
const NEW_TAB_REL = 'noreferrer noopener';

function URLPicker( {
	isSelected,
	url,
	updateBlock,
	opensInNewTab,
	onToggleOpenInNewTab,
	anchorRef,
} ) {
	const [ isURLPickerOpen, setIsURLPickerOpen ] = useState( false );
	const urlIsSet = !! url;
	const urlIsSetandSelected = urlIsSet && isSelected;
	const openLinkControl = () => {
		setIsURLPickerOpen( true );
		return false; // prevents default behaviour for event
	};
	const unlinkButton = () => {
		updateBlock( {
			url: undefined,
			linkTarget: undefined,
			rel: undefined,
		} );
		setIsURLPickerOpen( false );
	};
	const linkControl = ( isURLPickerOpen || urlIsSetandSelected ) && (
		<Popover
			position="bottom center"
			onClose={ () => setIsURLPickerOpen( false ) }
			anchorRef={ anchorRef?.current }
		>
			<LinkControl
				className="wp-block-navigation-link__inline-link-input"
				value={ { url, opensInNewTab } }
				onChange={ ( {
					url: newURL = '',
					opensInNewTab: newOpensInNewTab,
				} ) => {
					updateBlock( { url: newURL } );

					if ( opensInNewTab !== newOpensInNewTab ) {
						onToggleOpenInNewTab( newOpensInNewTab );
					}
				} }
			/>
		</Popover>
	);
	return (
		<>
			<BlockControls group="block">
				{ ! urlIsSet && (
					<ToolbarButton
						name="link"
						icon={ link }
						title={ __( 'Link' ) }
						shortcut={ displayShortcut.primary( 'k' ) }
						onClick={ openLinkControl }
					/>
				) }
				{ urlIsSetandSelected && (
					<ToolbarButton
						name="link"
						icon={ linkOff }
						title={ __( 'Unlink' ) }
						shortcut={ displayShortcut.primaryShift( 'k' ) }
						onClick={ unlinkButton }
						isActive={ true }
					/>
				) }
			</BlockControls>
			{ isSelected && (
				<KeyboardShortcuts
					bindGlobal
					shortcuts={ {
						[ rawShortcut.primary( 'k' ) ]: openLinkControl,
						[ rawShortcut.primaryShift( 'k' ) ]: unlinkButton,
					} }
				/>
			) }
			{ linkControl }
		</>
	);
}

const URLPickerControl = ( props ) => {
	const { attributes, isSelected, hasURLPicker = true, updateBlock } = props;
	const { url, linkTarget, rel } = attributes;

	const onToggleOpenInNewTab = useCallback(
		( value ) => {
			const newLinkTarget = value ? '_blank' : undefined;

			let updatedRel = rel;
			if ( newLinkTarget && ! rel ) {
				updatedRel = NEW_TAB_REL;
			} else if ( ! newLinkTarget && rel === NEW_TAB_REL ) {
				updatedRel = undefined;
			}

			updateBlock( {
				linkTarget: newLinkTarget,
				rel: updatedRel,
			} );
		},
		[ rel, updateBlock ]
	);
	const ref = useRef();
	return <>{ hasURLPicker && <URLPicker
		url={ url }
		updateBlock={ updateBlock }
		isSelected={ isSelected }
		opensInNewTab={ linkTarget === '_blank' }
		onToggleOpenInNewTab={ onToggleOpenInNewTab }
		anchorRef={ ref }
	/> }</>;
};

addFilter(
	`gutenify-block-${ hookPrefix }-block-controls`,
	`Gutenify/Block/${ hookPrefix }/InspectorControls/BlockControls/urlpicker`,
	( content, props ) => {
		return [
			...content,
			<Fragment key={ `gutenify-block-${ hookPrefix }-options-block-control-url-picker` }>
				<URLPickerControl { ...props } />
			</Fragment>,
		];
	}
);
